import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
    Button,
    Checkbox,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Input,
    SearchInput,
    Select,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchProviderSessions } from "../../../../hooks/queries";
import FilterButton from "../../../../shared-ui/Buttons/FilterButton/FilterButton";
import {
    displayNameInRightFormat,
    formatZonedTimeToUtc,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import {
    IConfirmSchedulePayload,
    IConfirmSchedulePayloadForm,
} from "../../types/dashboard.types";
import { useConfirmSchedule } from "../../hooks/dashboard.mutations";
import useToast from "../../../../hooks/useToast";
import { useDebounce } from "../../../../hooks/helpers";
import { getRecurrenceTag } from "../../helpers/dashboard.helpers";
import moment, { GetStartAndEndOfCurrentWeek } from "../../../../utils/moment";
import ListState from "../../../../components/ListState/ListState";
import { RecurrenceRule } from "../../../../utils/types";
import PlusIcon from "../../../../components/Icons/Plus";
import { APP_COLORS } from "../../../../utils/constants";
import TimesIcon from "../../../../components/Icons/Times";
import { IProviderSession } from "../../../../utils/types/session";

interface ConfirmScheduleFormProps {
    onFormSubmit(): void;
}

type Options = {
    label: string;
    value: string | undefined;
};

const schema = yup.object({
    sessions: yup.array().of(
        yup.object().shape({
            session_id: yup.string(),
            start_date_time: yup
                .string()
                .required("Session start time is required"),
            end_date_time: yup
                .string()
                .required("Session end time is required")
                .test({
                    name: "is-greater",
                    message:
                        "Session end time should be later than the start time",
                    test: (
                        value: string | undefined,
                        context: {
                            parent: {
                                start_date_time: string;
                            };
                        }
                    ) => {
                        const { start_date_time: sessionStartTime } =
                            context.parent;
                        if (!sessionStartTime || !value) {
                            return true;
                        }
                        const start = new Date(
                            `1970-01-01T${sessionStartTime}:00`
                        );
                        const end = new Date(`1970-01-01T${value}:00`);
                        return end > start;
                    },
                }),
            session_day: yup.string().required("Field is required"),
        })
    ),
    extra_sessions: yup
        .array()
        .of(
            yup.object().shape({
                start_date_time: yup.string().required("Field is required"),
                end_date_time: yup
                    .string()
                    .required("Field is required")
                    .test({
                        name: "is-greater",
                        message: "End time should be later than the start time",
                        test: (
                            value: string | undefined,
                            context: {
                                parent: {
                                    start_date_time: string;
                                };
                            }
                        ) => {
                            const { start_date_time: sessionStartTime } =
                                context.parent;
                            if (!sessionStartTime || !value) {
                                return true;
                            }
                            const start = new Date(
                                `1970-01-01T${sessionStartTime}:00`
                            );
                            const end = new Date(`1970-01-01T${value}:00`);
                            return end > start;
                        },
                    }),
                session_day: yup.string().required("Field is required"),
            })
        )
        .optional()
        .default([]),
});

export default function ConfirmScheduleForm({
    onFormSubmit,
}: ConfirmScheduleFormProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchString, setSearchString] = React.useState<string>("");

    const { startOfWeek, endOfWeek } = GetStartAndEndOfCurrentWeek();

    const recurrenceFilter = searchParams.get("recurrence") || "";
    const searchFilter = useDebounce(searchString, 500);

    const {
        data: sessions,
        isLoading,
        isSuccess,
    } = useFetchProviderSessions({
        search_string: searchFilter,
        recurrence_rule: recurrenceFilter,
        show_supervisee_sessions: "false",
        include_inactive_sessions: "false",
        confirmed_start_date_time: startOfWeek,
        confirmed_end_date_time: endOfWeek,
        session_type: "unconfirmed",
    });

    const [checkedSessions, setCheckedSessions] = React.useState<string[]>([]);

    const {
        control,
        handleSubmit,
        register,
        watch,
        reset,
        formState: { errors },
    } = useForm<IConfirmSchedulePayloadForm>({
        resolver: yupResolver(schema),
    });

    const {
        fields: extraSessions,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "extra_sessions",
    });

    const handleAddExtraSession = React.useCallback(
        (sessionId: string) => {
            append({
                session_id: sessionId,
                start_date_time: "",
                end_date_time: "",
                session_day: "",
            });
        },
        [append]
    );

    const handleRemoveExtraSession = React.useCallback(
        (sessionIndex: number) => {
            remove(sessionIndex);
        },
        [remove]
    );

    const getDayOrder = (dayAbbreviation: string) => {
        const dayOrder = {
            Mon: 1,
            Tue: 2,
            Wed: 3,
            Thu: 4,
            Fri: 5,
            Sat: 6,
            Sun: 7,
        };
        return dayOrder[dayAbbreviation as keyof typeof dayOrder];
    };

    const timeToMinutes = (hour: number, minute: number) => {
        return hour * 60 + minute;
    };

    const sortSessions = React.useCallback(
        (sessionList: IProviderSession[]) => {
            return (
                sessionList.sort((a, b) => {
                    const dayOrderA = getDayOrder(a.session_day);
                    const dayOrderB = getDayOrder(b.session_day);

                    if (dayOrderA !== dayOrderB) {
                        return dayOrderA - dayOrderB;
                    }

                    const timeA = timeToMinutes(
                        a.session_start_hr,
                        a.session_start_min
                    );
                    const timeB = timeToMinutes(
                        b.session_start_hr,
                        b.session_start_min
                    );

                    return timeA - timeB;
                }) || []
            );
        },
        []
    );

    const recurrenceRuleForSelect = Object.values(RecurrenceRule).filter(
        (item) =>
            item !== RecurrenceRule.TWO_MONTHS &&
            item !== RecurrenceRule.THREE_MONTHS
    );

    const daysOfWeek = React.useMemo((): Options[] => {
        return Array.from({ length: 7 }, (_, i) => {
            const date = moment().startOf("isoWeek").add(i, "days");
            return {
                label: date.format("dddd"),
                value: date.toISOString(),
            };
        });
    }, []);

    const currentWeekRange = `${startOfWeek} - ${endOfWeek}`;

    const handleRecurrenceChange = (value: string) => {
        if (value === "") searchParams.delete("recurrence");
        else searchParams.set("recurrence", value);

        setSearchParams(searchParams);
    };

    const { mutate } = useConfirmSchedule();

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const formatTimeToCorrectFormat = (time: number) => {
        if (time < 10) return `0${time}`;
        return time;
    };

    const handleSessionCheck = (sessionId: string) => {
        setCheckedSessions((prev) =>
            prev.includes(sessionId)
                ? prev.filter((id) => id !== sessionId)
                : [...prev, sessionId]
        );
    };

    const getExtraSessionsForSession = React.useMemo(() => {
        return (sessionId: string) => {
            return extraSessions.filter((s) => s.session_id === sessionId);
        };
    }, [extraSessions]);

    const onSubmit = (data: IConfirmSchedulePayload) => {
        if (checkedSessions.length === 0) {
            toast({
                mode: "error",
                message: "Please select at least one session",
            });
            return;
        }

        const formattedSessions =
            data.sessions &&
            data.sessions
                .filter((sessionData) =>
                    checkedSessions.includes(sessionData.session_id)
                )
                .map((sessionData) => {
                    const originalSession = sessions?.data.find(
                        (s) => s.session_id === sessionData.session_id
                    );

                    const sessionWeekday = sessionData.session_day
                        ? moment(sessionData.session_day).format("dddd")
                        : moment(originalSession?.start_date_time).format(
                              "dddd"
                          );

                    const sessionDateThisWeek = moment()
                        .isoWeekday(sessionWeekday)
                        .format("YYYY-MM-DD");

                    const [startHour, startMinute] =
                        sessionData.start_date_time.split(":");
                    const formattedStartTime = formatZonedTimeToUtc(
                        `${sessionDateThisWeek}T${startHour}:${startMinute}:00.000`
                    );

                    const [endHour, endMinute] =
                        sessionData.end_date_time.split(":");
                    const formattedEndTime = formatZonedTimeToUtc(
                        `${sessionDateThisWeek}T${endHour}:${endMinute}:00.000`
                    );

                    return {
                        session_id: sessionData.session_id,
                        start_date_time: formattedStartTime,
                        end_date_time: formattedEndTime,
                        session_day: sessionDateThisWeek,
                    };
                });

        const extraSessionsForm = watch("extra_sessions");

        const extraSessionsData =
            extraSessionsForm && extraSessions.length > 0
                ? extraSessionsForm.map((sessionData) => {
                      const originalSession = sessions?.data.find(
                          (s) => s.session_id === sessionData.session_id
                      );

                      const sessionWeekday = sessionData.session_day
                          ? moment(sessionData.session_day).format("dddd")
                          : moment(originalSession?.start_date_time).format(
                                "dddd"
                            );

                      const sessionDateThisWeek = moment()
                          .isoWeekday(sessionWeekday)
                          .format("YYYY-MM-DD");

                      const [startHour, startMinute] =
                          sessionData.start_date_time.split(":");
                      const formattedStartTime = formatZonedTimeToUtc(
                          `${sessionDateThisWeek}T${startHour}:${startMinute}:00.000`
                      );
                      const [endHour, endMinute] =
                          sessionData.end_date_time.split(":");
                      const formattedEndTime = formatZonedTimeToUtc(
                          `${sessionDateThisWeek}T${endHour}:${endMinute}:00.000`
                      );
                      return {
                          session_id: sessionData.session_id,
                          start_date_time: formattedStartTime,
                          end_date_time: formattedEndTime,
                          session_day: sessionDateThisWeek,
                      };
                  })
                : [];

        mutate(
            { sessions: [...formattedSessions, ...extraSessionsData] },
            {
                onSuccess: async (res) => {
                    toast({
                        mode: "success",
                        message: res.message || "Schedule confirmed",
                    });

                    queryClient.invalidateQueries({
                        queryKey: ["provider-sessions"],
                    });

                    queryClient.invalidateQueries({
                        queryKey: ["caseload"],
                    });

                    queryClient.invalidateQueries({
                        queryKey: ["provider-upcoming-sessions"],
                    });

                    onFormSubmit();
                },
                onError: (error) => {
                    const unconfirmedSessions =
                        error?.response?.data?.data?.unconfirmed_sessions;

                    toast({
                        mode: "error",
                        message: (
                            <div>
                                {unconfirmedSessions &&
                                unconfirmedSessions.length > 0
                                    ? unconfirmedSessions.map((s) => (
                                          <div key={s.session_id}>
                                              {s.error}
                                          </div>
                                      ))
                                    : "Could not confirm schedule"}
                            </div>
                        ),
                    });
                },
            }
        );
    };

    React.useEffect(() => {
        if (sessions?.data) {
            reset({
                sessions: sortSessions(sessions.data).map((session) => ({
                    session_id: session.session_id,
                    session_day: daysOfWeek.find((day) =>
                        day.label.startsWith(session.session_day)
                    )?.value,
                    start_date_time: `${formatTimeToCorrectFormat(
                        session.session_start_hr
                    )}:${formatTimeToCorrectFormat(session.session_start_min)}`,
                    end_date_time: `${formatTimeToCorrectFormat(
                        session.session_end_hr
                    )}:${formatTimeToCorrectFormat(session.session_end_min)}`,
                })),
            });
        }
    }, [sessions?.data, reset, daysOfWeek, sortSessions]);

    return (
        <div>
            <div className="flex items-center justify-between">
                <SearchInput
                    placeholder="Search by client name"
                    onChange={(e) => {
                        reset();
                        setCheckedSessions([]);
                        setSearchString(e.target.value);
                    }}
                />
                <Dropdown>
                    <DropdownTrigger asChild>
                        <FilterButton
                            text={
                                recurrenceFilter
                                    ? truncateString(
                                          removeEnumUnderscore(recurrenceFilter)
                                      )
                                    : "All"
                            }
                        />
                    </DropdownTrigger>
                    <DropdownContent
                        align="end"
                        width={200}
                        maxHeight={216}
                        className="overflow-y-auto"
                    >
                        <DropdownItem
                            isSelected={recurrenceFilter === ""}
                            onClick={() => handleRecurrenceChange("")}
                        >
                            All
                        </DropdownItem>
                        {recurrenceRuleForSelect.map((rule) => (
                            <DropdownItem
                                key={rule}
                                isSelected={recurrenceFilter === rule}
                                onClick={() => handleRecurrenceChange(rule)}
                            >
                                {removeEnumUnderscore(rule)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            </div>
            {isLoading && (
                <div className="flex items-center justify-center h-[200px]">
                    <span className="mid-loader" />
                </div>
            )}
            {sessions && (
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    id="confirm-schedule"
                    className="mt-16"
                >
                    <div className="text-xs font-medium">
                        {`${checkedSessions.length} Sessions selected for: ${currentWeekRange}`}
                    </div>
                    {sessions.data.length === 0 ? (
                        <ListState
                            isError={false}
                            stateHelperText="Sessions will appear here when available"
                            emptyMessage="No sessions yet"
                        />
                    ) : null}
                    {isSuccess && sessions.data.length > 0 ? (
                        <div className="flex flex-col gap-y-8 mt-8">
                            {sortSessions(sessions.data).map((session, i) => {
                                return (
                                    <div
                                        key={session.session_id}
                                        className="px-12 py-[10px] bg-[rgba(241,_241,_243,_0.40)] rounded-r12 border border-strokedark"
                                    >
                                        <div className="flex lg:items-center justify-between items-start flex-col lg:flex-row gap-x-8 gap-y-12">
                                            <div className="flex items-center gap-x-10">
                                                <Checkbox
                                                    name=""
                                                    value=""
                                                    isChecked={checkedSessions.includes(
                                                        session.session_id
                                                    )}
                                                    handleChange={() =>
                                                        handleSessionCheck(
                                                            session.session_id
                                                        )
                                                    }
                                                />
                                                <span className="text-sm font-medium">
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            session.client
                                                                .first_name,
                                                        lastName:
                                                            session.client
                                                                .last_name,
                                                    })}
                                                </span>
                                                <span>
                                                    {getRecurrenceTag(
                                                        session.recurrence_rule
                                                    )}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-x-8 flex-wrap gap-y-12">
                                                <input
                                                    type="hidden"
                                                    {...register(
                                                        `sessions.${i}.session_id`
                                                    )}
                                                    value={session.session_id}
                                                />
                                                <div className="w-auto">
                                                    <Controller
                                                        name={`sessions.${i}.session_day`}
                                                        control={control}
                                                        defaultValue={
                                                            daysOfWeek.find(
                                                                (day) =>
                                                                    day.label.startsWith(
                                                                        session.session_day
                                                                    )
                                                            )?.value
                                                        }
                                                        render={({ field }) => (
                                                            <Select
                                                                options={
                                                                    daysOfWeek
                                                                }
                                                                placeholder=""
                                                                onChange={(
                                                                    val
                                                                ) =>
                                                                    field.onChange(
                                                                        (
                                                                            val as Options
                                                                        ).value
                                                                    )
                                                                }
                                                                defaultValue={daysOfWeek.find(
                                                                    (day) =>
                                                                        day.label.startsWith(
                                                                            session.session_day
                                                                        )
                                                                )}
                                                                isLongListInDialog
                                                                classNames={{
                                                                    container:
                                                                        () =>
                                                                            "w-[140px] lg:w-[120px]",
                                                                    control:
                                                                        () =>
                                                                            "!h-32",
                                                                    menu: () =>
                                                                        "!w-[140px] lg:!w-[120px]",
                                                                    menuPortal:
                                                                        () =>
                                                                            "!z-[60]",
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="w-auto">
                                                    <Input
                                                        {...register(
                                                            `sessions.${i}.start_date_time`
                                                        )}
                                                        type="time"
                                                        className="h-32 px-12"
                                                        defaultValue={`${formatTimeToCorrectFormat(
                                                            session.session_start_hr
                                                        )}:${formatTimeToCorrectFormat(
                                                            session.session_start_min
                                                        )}`}
                                                    />
                                                </div>
                                                -
                                                <div className="w-auto">
                                                    <Input
                                                        {...register(
                                                            `sessions.${i}.end_date_time`
                                                        )}
                                                        type="time"
                                                        className="h-32 px-12"
                                                        defaultValue={`${formatTimeToCorrectFormat(
                                                            session.session_end_hr
                                                        )}:${formatTimeToCorrectFormat(
                                                            session.session_end_min
                                                        )}`}
                                                    />
                                                </div>
                                                {checkedSessions.includes(
                                                    session.session_id
                                                ) ? (
                                                    <Button
                                                        variant="normal"
                                                        size="auto"
                                                        className="h-32 px-12 border border-primary"
                                                        onClick={() =>
                                                            handleAddExtraSession(
                                                                session.session_id
                                                            )
                                                        }
                                                    >
                                                        <PlusIcon
                                                            stroke={
                                                                APP_COLORS.COLOR_PRIMARY
                                                            }
                                                        />
                                                    </Button>
                                                ) : null}
                                            </div>
                                            {errors.sessions?.[i]
                                                ?.start_date_time ? (
                                                <p className=" text-danger">
                                                    {
                                                        errors.sessions?.[i]
                                                            ?.start_date_time
                                                            ?.message
                                                    }
                                                </p>
                                            ) : null}
                                            {errors.sessions?.[i]
                                                ?.end_date_time ? (
                                                <p className=" text-danger">
                                                    {
                                                        errors.sessions?.[i]
                                                            ?.end_date_time
                                                            ?.message
                                                    }
                                                </p>
                                            ) : null}
                                        </div>
                                        {getExtraSessionsForSession(
                                            session.session_id
                                        )?.length > 0 ? (
                                            <div className="mt-12 flex flex-col gap-y-8">
                                                {getExtraSessionsForSession(
                                                    session.session_id
                                                ).map((extra) => {
                                                    const itemCorrectIndex =
                                                        extraSessions.findIndex(
                                                            (ext) =>
                                                                ext.id ===
                                                                extra.id
                                                        );
                                                    return (
                                                        <div
                                                            key={extra.id}
                                                            className="flex items-center gap-x-8 flex-wrap gap-y-12 lg:justify-end"
                                                        >
                                                            <div className="w-auto">
                                                                <Controller
                                                                    name={`extra_sessions.${itemCorrectIndex}.session_day`}
                                                                    control={
                                                                        control
                                                                    }
                                                                    render={({
                                                                        field,
                                                                    }) => (
                                                                        <Select
                                                                            options={
                                                                                daysOfWeek
                                                                            }
                                                                            placeholder="Select day"
                                                                            value={daysOfWeek.find(
                                                                                (
                                                                                    day
                                                                                ) =>
                                                                                    day.value ===
                                                                                    field.value
                                                                            )}
                                                                            onChange={(
                                                                                val
                                                                            ) => {
                                                                                field.onChange(
                                                                                    (
                                                                                        val as Options
                                                                                    )
                                                                                        .value
                                                                                );
                                                                            }}
                                                                            isLongListInDialog
                                                                            classNames={{
                                                                                container:
                                                                                    () =>
                                                                                        "w-[140px] lg:w-[120px]",
                                                                                control:
                                                                                    () =>
                                                                                        "!h-32",
                                                                                menu: () =>
                                                                                    "w-[140px] lg:w-[120px]",
                                                                                menuPortal:
                                                                                    () =>
                                                                                        "!z-[60]",
                                                                            }}
                                                                            hasError={
                                                                                !!errors
                                                                                    .extra_sessions?.[
                                                                                    itemCorrectIndex
                                                                                ]
                                                                                    ?.session_day
                                                                            }
                                                                        />
                                                                    )}
                                                                />
                                                            </div>
                                                            <div className="w-auto">
                                                                <Input
                                                                    {...register(
                                                                        `extra_sessions.${itemCorrectIndex}.start_date_time`
                                                                    )}
                                                                    type="time"
                                                                    className="h-32 px-12"
                                                                    hasError={
                                                                        !!errors
                                                                            .extra_sessions?.[
                                                                            itemCorrectIndex
                                                                        ]
                                                                            ?.start_date_time
                                                                    }
                                                                />
                                                            </div>
                                                            -
                                                            <div className="w-auto">
                                                                <Input
                                                                    {...register(
                                                                        `extra_sessions.${itemCorrectIndex}.end_date_time`
                                                                    )}
                                                                    type="time"
                                                                    className="h-32 px-12"
                                                                    hasError={
                                                                        !!errors
                                                                            .extra_sessions?.[
                                                                            itemCorrectIndex
                                                                        ]
                                                                            ?.end_date_time
                                                                    }
                                                                />
                                                            </div>
                                                            <Button
                                                                variant="normal"
                                                                size="auto"
                                                                className="h-32 px-12 border border-danger"
                                                                onClick={() =>
                                                                    handleRemoveExtraSession(
                                                                        itemCorrectIndex
                                                                    )
                                                                }
                                                            >
                                                                <TimesIcon
                                                                    stroke={
                                                                        APP_COLORS.COLOR_DANGER
                                                                    }
                                                                />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </form>
            )}
        </div>
    );
}
